import { parseManualDateInput } from "@/components/dateTime/genericDateFilterUtils";

export type DateInputSegment =
  | "day"
  | "month"
  | "year"
  | "hour"
  | "minute";

export interface DateInputSelection {
  start: number;
  end: number;
}

export interface DateSegmentDigitResult {
  accepted: boolean;
  value: string;
  complete: boolean;
}

type TwoDigitInputSegment = Exclude<DateInputSegment, "year">;

const MAXIMUM_LEADING_DIGITS: Record<TwoDigitInputSegment, number> = {
  day: 3,
  month: 1,
  hour: 2,
  minute: 5,
};

const SEGMENT_RANGES: Record<
  TwoDigitInputSegment,
  { minimum: number; maximum: number }
> = {
  day: { minimum: 1, maximum: 31 },
  month: { minimum: 1, maximum: 12 },
  hour: { minimum: 0, maximum: 23 },
  minute: { minimum: 0, maximum: 59 },
};

export const DATE_INPUT_SEGMENTS: DateInputSegment[] = [
  "day",
  "month",
  "year",
];

export const DATE_TIME_INPUT_SEGMENTS: DateInputSegment[] = [
  ...DATE_INPUT_SEGMENTS,
  "hour",
  "minute",
];

export const splitDateInputValue = (
  value: string,
): { datePart: string; suffix: string } => {
  const suffixStart = value.indexOf(",");
  if (suffixStart === -1) {
    return { datePart: value, suffix: "" };
  }

  return {
    datePart: value.slice(0, suffixStart),
    suffix: value.slice(suffixStart),
  };
};

const getDateParts = (value: string): [string, string, string] | null => {
  const { datePart } = splitDateInputValue(value);
  const parts = datePart.split(".");
  if (parts.length !== 3) {
    return null;
  }

  return [parts[0], parts[1], parts[2]];
};

const getTimeParts = (
  value: string,
): { hour: string; minute: string; start: number } | null => {
  const separatorIndex = value.indexOf(",");
  if (separatorIndex === -1) {
    return null;
  }

  let start = separatorIndex + 1;
  while (value[start] === " ") {
    start += 1;
  }

  const parts = value.slice(start).split(":");
  if (parts.length !== 2) {
    return null;
  }

  return { hour: parts[0], minute: parts[1], start };
};

export const getDateInputSegmentSelection = (
  value: string,
  segment: DateInputSegment,
): DateInputSelection | null => {
  if (segment === "hour" || segment === "minute") {
    const timeParts = getTimeParts(value);
    if (!timeParts) {
      return null;
    }

    if (segment === "hour") {
      return {
        start: timeParts.start,
        end: timeParts.start + timeParts.hour.length,
      };
    }

    const minuteStart = timeParts.start + timeParts.hour.length + 1;
    return {
      start: minuteStart,
      end: minuteStart + timeParts.minute.length,
    };
  }

  const parts = getDateParts(value);
  if (!parts) {
    return null;
  }

  const [day, month, year] = parts;
  const selections: Record<
    Exclude<DateInputSegment, "hour" | "minute">,
    DateInputSelection
  > = {
    day: { start: 0, end: day.length },
    month: {
      start: day.length + 1,
      end: day.length + 1 + month.length,
    },
    year: {
      start: day.length + month.length + 2,
      end: day.length + month.length + 2 + year.length,
    },
  };

  return selections[segment];
};

export const getDateInputSegmentAtPosition = (
  value: string,
  position: number,
  segments: DateInputSegment[] = DATE_INPUT_SEGMENTS,
): DateInputSegment | null => {
  if (position < 0 || position > value.length) {
    return null;
  }

  for (const segment of segments) {
    const selection = getDateInputSegmentSelection(value, segment);
    if (
      selection &&
      position >= selection.start &&
      position <= selection.end
    ) {
      return segment;
    }
  }

  return null;
};

export const replaceDateInputSegment = (
  value: string,
  segment: DateInputSegment,
  replacement: string,
): string => {
  const selection = getDateInputSegmentSelection(value, segment);
  if (!selection) {
    return value;
  }

  return `${value.slice(0, selection.start)}${replacement}${value.slice(
    selection.end,
  )}`;
};

const rejectedDigitResult = (value: string): DateSegmentDigitResult => ({
  accepted: false,
  value,
  complete: false,
});

export const applyDigitToDateSegment = (
  segment: DateInputSegment,
  currentBuffer: string,
  digit: string,
): DateSegmentDigitResult => {
  if (!/^\d$/.test(digit)) {
    return rejectedDigitResult(currentBuffer);
  }

  if (segment !== "year") {
    if (currentBuffer.length === 0) {
      const numericDigit = Number(digit);
      const maximumLeadingDigit = MAXIMUM_LEADING_DIGITS[segment];

      if (numericDigit > maximumLeadingDigit) {
        return { accepted: true, value: `0${digit}`, complete: true };
      }

      return { accepted: true, value: digit, complete: false };
    }

    if (currentBuffer.length !== 1) {
      return rejectedDigitResult(currentBuffer);
    }

    const candidate = `${currentBuffer}${digit}`;
    const numericCandidate = Number(candidate);
    const range = SEGMENT_RANGES[segment];
    if (
      numericCandidate < range.minimum ||
      numericCandidate > range.maximum
    ) {
      return rejectedDigitResult(currentBuffer);
    }

    return { accepted: true, value: candidate, complete: true };
  }

  if (currentBuffer.length === 0 && digit !== "1" && digit !== "2") {
    return rejectedDigitResult(currentBuffer);
  }
  if (currentBuffer.length >= 4) {
    return rejectedDigitResult(currentBuffer);
  }

  const candidate = `${currentBuffer}${digit}`;
  return {
    accepted: true,
    value: candidate,
    complete: candidate.length === 4,
  };
};

export const normalizePastedDateSegment = (
  segment: DateInputSegment,
  pastedValue: string,
): string | null => {
  if (!/^\d+$/.test(pastedValue)) {
    return null;
  }

  if (segment === "year") {
    return /^[12]\d{3}$/.test(pastedValue) ? pastedValue : null;
  }

  if (pastedValue.length > 2) {
    return null;
  }

  const numericValue = Number(pastedValue);
  const range = SEGMENT_RANGES[segment];
  if (numericValue < range.minimum || numericValue > range.maximum) {
    return null;
  }

  return String(numericValue).padStart(2, "0");
};

export const formatPastedDate = (pastedValue: string): string | null => {
  const trimmed = pastedValue.trim();
  const isSupportedDateOnlyFormat =
    /^\d{8}$/.test(trimmed) ||
    /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(trimmed) ||
    /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ||
    /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed);

  if (!isSupportedDateOnlyFormat) {
    return null;
  }

  const parsed = parseManualDateInput(trimmed);
  return parsed ? parsed.format("DD.MM.YYYY") : null;
};
